import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Checkbox, Drawer, FormControlLabel, GlobalStyles, Typography, useMediaQuery } from "@mui/material";
import { useSnackbar } from "notistack";
import { Upload, Wand2 } from "lucide-react";
import sh from "bundles/common/utils/sh";
import { uploadCvToVault } from "bundles/Internal/utils/uploadCvToVault";
import { useCandidateModal } from "bundles/Internal/components/common/CandidateModalProvider";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import VaultFilterBar, { DEFAULT_STAGE, STAGE_STATUSES } from "bundles/Internal/components/cv_vault/VaultFilterBar";
import VaultList from "bundles/Internal/components/cv_vault/VaultList";
import VaultDetail from "bundles/Internal/components/cv_vault/VaultDetail";
import { BRANDED_LOCALES } from "bundles/Internal/components/common/BrandedCvMenu";
import { DEFAULT_PARSER_MODEL, ParserModelPicker } from "./common/ParserModelMenu";
import consumer from "bundles/Internal/channels/consumer";

const PAGE_SIZE = 50;
const ROW_H = 68;
// Images and scanned PDFs have no text layer; they go through OCR on the way in.
const ACCEPTED = /\.(pdf|docx|png|jpe?g|webp|tiff?)$/i;

const formatMinutes = (seconds) => {
  if (seconds < 60) return "under a minute";
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `about ${mins} min`;
  const hours = Math.floor(mins / 60);
  return `about ${hours}h${String(mins % 60).padStart(2, "0")}`;
};

const CvVaultPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { openCreateCandidate } = useCandidateModal();
  const splitView = useMediaQuery("(min-width:900px)");

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [expected, setExpected] = useState({ p50: 60, p90: 120, concurrency: 1 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [search, setSearch] = useState("");
  const [stage, setStage] = useState(DEFAULT_STAGE);
  const [selected, setSelected] = useState([]);
  const [detailItem, setDetailItem] = useState(null);
  const [editingBranded, setEditingBranded] = useState(false);

  const [uploading, setUploading] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [confirmExtractAll, setConfirmExtractAll] = useState(false);
  const [confirmBulkExtract, setConfirmBulkExtract] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [confirmBrandedBulk, setConfirmBrandedBulk] = useState(false);
  const [confirmOcrBulk, setConfirmOcrBulk] = useState(false);
  const [ocrBulkIds, setOcrBulkIds] = useState([]);
  const [ocrThenBranded, setOcrThenBranded] = useState(false);
  const [bulkLocale, setBulkLocale] = useState("");

  const [bulkModel, setBulkModel] = useState(DEFAULT_PARSER_MODEL);

  const scrollRef = useRef(null);
  const dragDepth = useRef(0);
  const inFlight = useRef(false);
  const pageRef = useRef(1);
  const lastPageRef = useRef(1);
  const searchRef = useRef("");

  const statusFilter = stage ? STAGE_STATUSES[stage] : null;

  const searchParams = useCallback(
    (page) => ({
      query: search || undefined,
      status: statusFilter || undefined,
      page,
      limit: PAGE_SIZE,
    }),
    [search, statusFilter]
  );

  const loadFirstPage = useCallback(() => {
    inFlight.current = true;
    setLoading(true);

    sh.get("/internal/cv_vault_items", { params: searchParams(1) })
      .then((res) => {
        const data = res.data || {};
        setItems(data.items || []);
        setStats(data.stats || {});
        setFilteredTotal(data.pagy?.count || 0);
        pageRef.current = 1;
        lastPageRef.current = data.pagy?.last || 1;
        if (data.expected) setExpected(data.expected);
        setLoading(false);
        setLoadingMore(false);
        inFlight.current = false;
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      })
      .catch(() => {
        setLoading(false);
        setLoadingMore(false);
        inFlight.current = false;
      });
  }, [searchParams]);

  useEffect(() => {
    const timer = setTimeout(loadFirstPage, 250);
    return () => clearTimeout(timer);
  }, [loadFirstPage]);

  useEffect(() => {
    setSelected([]);
  }, [search, stage]);

  const loadMore = useCallback(() => {
    if (inFlight.current || pageRef.current >= lastPageRef.current) return;

    inFlight.current = true;
    setLoadingMore(true);
    const next = pageRef.current + 1;

    sh.get("/internal/cv_vault_items", { params: searchParams(next) })
      .then((res) => {
        const data = res.data || {};
        setItems((prev) => {
          const seen = new Set(prev.map((row) => row.id));
          return [...prev, ...(data.items || []).filter((row) => !seen.has(row.id))];
        });
        if (data.stats) setStats(data.stats);
        if (data.pagy?.count != null) setFilteredTotal(data.pagy.count);
        pageRef.current = next;
        lastPageRef.current = data.pagy?.last || next;
        setLoadingMore(false);
        inFlight.current = false;
      })
      .catch(() => {
        setLoadingMore(false);
        inFlight.current = false;
      });
  }, [searchParams]);

  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const extractableCount = stats.extractable || 0;
  const runningCount = stats.running || 0;
  const etaFor = (count) =>
    Math.ceil(count / Math.max(expected.concurrency || 1, 1)) * (expected.p50 || 60);
  const eta = etaFor(runningCount);

  const applyRow = useCallback((row) => {
    if (!row) return;
    setItems((prev) => prev.map((item) => (item.id === row.id ? row : item)));
    setDetailItem((prev) => (prev?.id === row.id ? row : prev));
  }, []);

  const patchItem = useCallback((id, partial) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...partial } : item)));
    setDetailItem((prev) => (prev?.id === id ? { ...prev, ...partial } : prev));
  }, []);

  const patchItems = useCallback((ids, partial) => {
    const set = new Set(ids);
    setItems((prev) => prev.map((item) => (set.has(item.id) ? { ...item, ...partial } : item)));
    setDetailItem((prev) => (prev && set.has(prev.id) ? { ...prev, ...partial } : prev));
  }, []);

  const dropItems = useCallback((ids) => {
    const set = new Set(ids);
    setItems((prev) => prev.filter((item) => !set.has(item.id)));
    setFilteredTotal((prev) => Math.max(0, prev - set.size));
    setDetailItem((prev) => (prev && set.has(prev.id) ? null : prev));
  }, []);

  const applyStats = useCallback((next) => {
    if (next) setStats(next);
  }, []);

  useEffect(() => {
    const subscription = consumer.subscriptions.create(
      { channel: "CvVaultItemsChannel" },
      {
        received(data) {
          if (data.stats && !searchRef.current) applyStats(data.stats);
          if (data.item) applyRow(data.item);
        },
      }
    );
    return () => subscription.unsubscribe();
  }, [applyRow, applyStats]);

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  }, []);

  const selectAll = useCallback(
    (next) => setSelected(next ? items.map((row) => row.id) : []),
    [items]
  );

  const acceptFiles = useCallback(
    async (files) => {
      const all = Array.from(files || []);
      const accepted = all.filter((file) => ACCEPTED.test(file.name));
      const skipped = all.length - accepted.length;

      if (skipped > 0) {
        enqueueSnackbar(
          `${skipped} file${skipped === 1 ? "" : "s"} skipped — only PDF, DOCX and images can be read`,
          { variant: "warning" }
        );
      }
      if (accepted.length === 0) return;

      setUploading(accepted.length);
      let inVault = 0;
      let onCandidate = 0;
      let added = 0;

      for (const file of accepted) {
        try {
          const result = await uploadCvToVault(file);
          if (result.duplicate) {
            if (result.duplicate.kind === "candidate") onCandidate += 1;
            else inVault += 1;
          } else if (result.item) {
            added += 1;
            const row = result.item;
            const visible = !statusFilter || statusFilter.includes(row.status);
            if (visible) {
              setItems((prev) => (prev.some((item) => item.id === row.id) ? prev : [row, ...prev]));
              setFilteredTotal((prev) => prev + 1);
            }
            setStats((prev) => ({
              ...prev,
              total: (prev.total || 0) + 1,
              [row.status]: (prev[row.status] || 0) + 1,
            }));
          }
        } catch {
          enqueueSnackbar(`Could not upload ${file.name}`, { variant: "error" });
        }
        setUploading((n) => n - 1);
      }

      if (inVault > 0) {
        enqueueSnackbar(`${inVault} CV${inVault === 1 ? " is" : "s are"} already in the vault, skipped`, { variant: "info" });
      }
      if (onCandidate > 0) {
        enqueueSnackbar(
          `${onCandidate} CV${onCandidate === 1 ? " already belongs" : "s already belong"} to a candidate, skipped`,
          { variant: "info" }
        );
      }
      if (added === 0 && inVault === 0 && onCandidate === 0) loadFirstPage();
    },
    [enqueueSnackbar, loadFirstPage, statusFilter]
  );

  const handleDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragActive(false);
    acceptFiles(e.dataTransfer?.files);
  };

  const extractOne = useCallback(
    (item, parserModel) => {
      sh.post(`/internal/cv_vault_items/${item.id}/extract`, { parser_model: parserModel || DEFAULT_PARSER_MODEL })
        .then((res) => {
          applyRow(res.data?.item);
          applyStats(res.data?.stats);
        })
        .catch((err) => enqueueSnackbar(err?.response?.data?.error || "Could not queue this CV", { variant: "error" }));
    },
    [applyRow, applyStats, enqueueSnackbar]
  );

  const ocrOne = useCallback(
    (item, then) => {
      sh.post(`/internal/cv_vault_items/${item.id}/ocr`, then ? { then } : {})
        .then((res) => {
          applyRow(res.data?.item);
          applyStats(res.data?.stats);
        })
        .catch((err) => enqueueSnackbar(err?.response?.data?.error || "Could not read this scan", { variant: "error" }));
    },
    [applyRow, applyStats, enqueueSnackbar]
  );

  const reextractOne = useCallback(
    (item) => {
      sh.post(`/internal/cv_vault_items/${item.id}/reextract`)
        .then((res) => {
          applyRow(res.data?.item);
          applyStats(res.data?.stats);
        })
        .catch((err) => enqueueSnackbar(err?.response?.data?.error || "Could not restart this CV", { variant: "error" }));
    },
    [applyRow, applyStats, enqueueSnackbar]
  );

  const retryOne = useCallback(
    (item, parserModel) => {
      if (item.status === "extracting_text") {
        reextractOne(item);
        return;
      }

      // A wedged OCR has no text yet, so restarting it means reading the scan again, not extracting.
      const restart = item.status === "ocring" ? () => ocrOne(item) : () => extractOne(item, parserModel);

      if (!["queued", "parsing", "ocring"].includes(item.status)) {
        extractOne(item, parserModel);
        return;
      }

      sh.post(`/internal/cv_vault_items/${item.id}/cancel`)
        .then(restart)
        .catch(() => enqueueSnackbar("Could not restart this CV", { variant: "error" }));
    },
    [enqueueSnackbar, extractOne, ocrOne, reextractOne]
  );

  const cancelOne = (item) => {
    sh.post(`/internal/cv_vault_items/${item.id}/cancel`)
      .then((res) => {
        applyRow(res.data?.item);
        applyStats(res.data?.stats);
        enqueueSnackbar("Extraction cancelled", { variant: "success" });
      })
      .catch(() => enqueueSnackbar("Could not cancel this CV", { variant: "error" }));
  };

  const extractAll = () => {
    setConfirmExtractAll(false);
    sh.post("/internal/cv_vault_items/extract_all", { parser_model: bulkModel })
      .then((res) => {
        enqueueSnackbar(`${res.data?.queued || 0} CVs queued, you can leave this page`, { variant: "success" });
        setStage("running");
      })
      .catch(() => enqueueSnackbar("Could not queue the CVs", { variant: "error" }));
  };

  const extractSelected = () => {
    const ids = selected;
    setConfirmBulkExtract(false);
    sh.post("/internal/cv_vault_items/bulk_extract", { ids, parser_model: bulkModel })
      .then((res) => {
        enqueueSnackbar(`${res.data?.queued || 0} CVs queued`, { variant: "success" });
        patchItems(ids, { status: "queued", stage: "queued", error: null, extractable: false });
        applyStats(res.data?.stats);
        setSelected([]);
      })
      .catch(() => enqueueSnackbar("Could not queue the CVs", { variant: "error" }));
  };

  const ocrSelected = () => {
    const ids = ocrBulkIds;
    setConfirmOcrBulk(false);
    sh.post("/internal/cv_vault_items/bulk_ocr", { ids, then: ocrThenBranded ? "branded_cv" : undefined })
      .then((res) => {
        enqueueSnackbar(`${res.data?.queued || 0} scans queued`, { variant: "success" });
        patchItems(ids, { status: "ocring", stage: null, error: null });
        applyStats(res.data?.stats);
        setSelected([]);
      })
      .catch(() => enqueueSnackbar("Could not queue the scans", { variant: "error" }));
  };

  const generateBranded = (item, locale, model) =>
    sh.post(`/internal/cv_vault_items/${item.id}/branded_cv`, {
      parser_model: model || DEFAULT_PARSER_MODEL,
      overrides: locale ? { locale } : undefined,
    })
      .then((res) => {
        enqueueSnackbar("Writing the Squarehub CV", { variant: "success" });
        patchItem(item.id, {
          branded_cv_status: res.data?.status || "generating",
          branded_cv_stage: null,
          branded_cv_progress: {},
          branded_cv_stalled: false,
          branded_cv_error: null,
        });
      })
      .catch(() => enqueueSnackbar("Could not start the Squarehub CV", { variant: "error" }));

  const generateBrandedSelected = () => {
    const ids = selected;
    setConfirmBrandedBulk(false);
    sh.post("/internal/cv_vault_items/bulk_branded_cv", {
      ids,
      parser_model: bulkModel,
      overrides: bulkLocale ? { locale: bulkLocale } : undefined,
    })
      .then((res) => {
        enqueueSnackbar(`${res.data?.queued || 0} Squarehub CVs queued`, { variant: "success" });
        patchItems(ids, {
          branded_cv_status: "generating",
          branded_cv_stage: null,
          branded_cv_progress: {},
          branded_cv_stalled: false,
          branded_cv_error: null,
        });
        setSelected([]);
      })
      .catch(() => enqueueSnackbar("Could not queue the Squarehub CVs", { variant: "error" }));
  };

  const deleteSelected = () => {
    const ids = selected;
    setConfirmBulkDelete(false);
    sh.post("/internal/cv_vault_items/bulk_destroy", { ids })
      .then((res) => {
        enqueueSnackbar(`${res.data?.deleted || 0} CVs deleted`, { variant: "success" });
        dropItems(ids);
        applyStats(res.data?.stats);
        setSelected([]);
      })
      .catch(() => enqueueSnackbar("Could not delete the CVs", { variant: "error" }));
  };

  const removeItem = () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    sh.delete(`/internal/cv_vault_items/${target.id}`)
      .then((res) => {
        dropItems([target.id]);
        applyStats(res.data?.stats);
      })
      .catch(() => enqueueSnackbar("Could not delete", { variant: "error" }));
  };

  // Two-arg then, not then().catch(): a chained catch also swallows anything the success handler
  // throws and reports a candidate that was created as a failure.
  const createCandidate = (values, { reset, close, autoEmail } = {}, vaultItemId = null) =>
    sh.post("/internal/candidates", { candidate: values, auto_email: autoEmail }).then(
      (res) => {
        enqueueSnackbar("Candidate created", { variant: "success" });
        reset?.();
        close?.();
        if (vaultItemId) {
          patchItem(vaultItemId, { status: "converted", candidate_id: res.data?.id, extractable: false });
        }
      },
      (err) => {
        const messages = err?.response?.data?.errors;
        if (Array.isArray(messages) && messages.length > 0) {
          messages.forEach((msg) => enqueueSnackbar(msg, { variant: "error" }));
          return;
        }
        enqueueSnackbar("Failed to create candidate", { variant: "error" });
      }
    );

  const convert = useCallback(
    (item) => {
      sh.get(`/internal/cv_vault_items/${item.id}`)
        .then((res) => {
          const data = res.data || {};
          openCreateCandidate({
            cvVaultItemId: item.id,
            cvFilename: item.filename,
            parsedPayload: { ...(data.payload || {}), detected_language: data.detected_language },
            cvText: data.cv_text,
            existingResumeUrl: data.file_url,
            onSubmit: (values, helpers) => createCandidate(values, helpers, item.id),
          });
        })
        .catch(() => enqueueSnackbar("Could not open this CV", { variant: "error" }));
    },
    [enqueueSnackbar, openCreateCandidate]
  );

  const openAt = useCallback(
    (index) => {
      const next = items[index];
      if (!next) return;

      setDetailItem(next);

      const el = scrollRef.current;
      if (!el) return;

      const top = index * ROW_H;
      if (top < el.scrollTop) el.scrollTop = top;
      else if (top + ROW_H > el.scrollTop + el.clientHeight) el.scrollTop = top + ROW_H - el.clientHeight;
    },
    [items]
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || event.target?.isContentEditable) return;
      if (items.length === 0) return;

      const index = detailItem ? items.findIndex((row) => row.id === detailItem.id) : -1;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        openAt(Math.min(index + 1, items.length - 1));
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        openAt(Math.max(index - 1, 0));
        return;
      }

      if (!detailItem) return;

      if (event.key === "Enter") {
        event.preventDefault();
        if (event.metaKey || event.ctrlKey) {
          if (detailItem.extractable) extractOne(detailItem, DEFAULT_PARSER_MODEL);
        } else if (detailItem.status === "parsed") {
          convert(detailItem);
        }
        return;
      }

      if ((event.key === "Delete" || event.key === "Backspace") && detailItem.status !== "converted") {
        event.preventDefault();
        setDeleteTarget(detailItem);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [items, detailItem, openAt, convert, extractOne]);

  const filtersActive = Boolean(search || (stage && stage !== DEFAULT_STAGE));

  const detail = (
    <VaultDetail
      item={detailItem}
      parserModel={DEFAULT_PARSER_MODEL}
      onExtract={extractOne}
      onConvert={convert}
      onCancel={cancelOne}
      onRetry={retryOne}
      onOcr={ocrOne}
      onDelete={setDeleteTarget}
      onGenerateBranded={generateBranded}
      onClose={() => setDetailItem(null)}
      editing={editingBranded}
      onEditingChange={setEditingBranded}
    />
  );

  return (
    <Box
      sx={{
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "hsl(140 10% 95%)",
        position: "relative",
      }}
      onDragEnter={(e) => {
        if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
        dragDepth.current += 1;
        setDragActive(true);
      }}
      onDragLeave={() => {
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragActive(false);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <GlobalStyles
        styles={{
          "@keyframes vault-spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
        }}
      />

      <Box
        sx={{
          flexShrink: 0,
          minHeight: 48,
          px: { xs: 1.5, md: 2 },
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          borderBottom: "1px solid hsl(140 8% 88%)",
          backgroundColor: "#ffffff",
        }}
      >
        <Typography
          sx={{
            fontSize: 15,
            flexShrink: 0,
            fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            color: "hsl(150 30% 12%)",
            letterSpacing: -0.2,
          }}
        >
          CV vault
        </Typography>

        <VaultFilterBar stats={stats} activeStage={stage} onSelect={setStage} />

        {runningCount > 0 && (
          <Typography noWrap sx={{ fontSize: 11.5, color: "hsl(217 70% 35%)", flexShrink: 0 }}>
            {runningCount} extracting · {formatMinutes(eta)} left
          </Typography>
        )}

        <Box sx={{ flex: 1, minWidth: 8 }} />

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}>
          <Box component="label" sx={{ display: "inline-flex" }}>
            <Button
              component="span"
              disableRipple
              startIcon={<Upload size={14} />}
              sx={{
                textTransform: "none",
                height: 32,
                px: 1.5,
                fontSize: 13,
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                borderRadius: "calc(-2px + 0.75rem)",
                color: "hsl(150 30% 12%)",
                backgroundColor: "#ffffff",
                border: "1px solid hsl(140 8% 88%)",
                "& .MuiButton-startIcon": { mr: 0.5 },
                "&:hover": { backgroundColor: "hsl(140 10% 96%)" },
              }}
            >
              Add CVs
            </Button>
            <input hidden multiple type="file" accept=".pdf,.docx,.png,.jpg,.jpeg,.webp,.tif,.tiff" onChange={(e) => acceptFiles(e.target.files)} />
          </Box>

          <Button
            disableRipple
            disableElevation
            startIcon={<Wand2 size={14} />}
            disabled={extractableCount === 0}
            onClick={() => setConfirmExtractAll(true)}
            sx={{
              textTransform: "none",
              height: 32,
              px: 1.5,
              fontSize: 13,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              borderRadius: "calc(-2px + 0.75rem)",
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              "& .MuiButton-startIcon": { mr: 0.5 },
              "&:hover": { backgroundColor: "hsl(150 38% 22%)" },
              "&:disabled": { opacity: 0.4, backgroundColor: "hsl(150 38% 17%)", color: "hsl(80 97% 72%)" },
            }}
          >
            Extract all{extractableCount > 0 ? ` (${extractableCount})` : ""}
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <Box sx={{ display: editingBranded ? "none" : "flex" }}>
          <VaultList
            items={items}
            loading={loading}
            loadingMore={loadingMore}
            total={filteredTotal}
            search={search}
            onSearchChange={setSearch}
            filtersActive={filtersActive}
            onClearFilters={() => {
              setSearch("");
              setStage(DEFAULT_STAGE);
            }}
            activeId={detailItem?.id}
            selected={selected}
            onToggleSelect={toggleSelect}
            onSelectAll={selectAll}
            onClearSelection={() => setSelected([])}
            onExtractSelected={() => setConfirmBulkExtract(true)}
            onOcrSelected={(eligibleIds) => { setOcrBulkIds(eligibleIds); setConfirmOcrBulk(true); }}
            onGenerateBrandedSelected={() => setConfirmBrandedBulk(true)}
            onDeleteSelected={() => setConfirmBulkDelete(true)}
            onOpen={setDetailItem}
            onLoadMore={loadMore}
            onFiles={acceptFiles}
            uploading={uploading}
            scrollRef={scrollRef}
          />
        </Box>

        {splitView && detail}
      </Box>

      {!splitView && (
        <Drawer
          anchor="right"
          open={Boolean(detailItem)}
          onClose={() => setDetailItem(null)}
          PaperProps={{
            sx: { width: "100vw", display: "flex", flexDirection: "column", backgroundColor: "hsl(140 10% 95%)" },
          }}
        >
          {detail}
        </Drawer>
      )}

      {dragActive && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "hsla(150, 38%, 17%, 0.12)",
            backdropFilter: "blur(2px)",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              px: 4,
              py: 3,
              borderRadius: "0.75rem",
              border: "2px dashed hsl(150 38% 17%)",
              backgroundColor: "#ffffff",
              textAlign: "center",
            }}
          >
            <Upload size={26} color="hsl(150 38% 17%)" />
            <Typography
              sx={{ fontSize: 14, mt: 1, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 38% 17%)" }}
            >
              Drop to add to the vault
            </Typography>
          </Box>
        </Box>
      )}

      <ConfirmDialog
        open={confirmExtractAll}
        onClose={() => setConfirmExtractAll(false)}
        onConfirm={extractAll}
        title={`Extract ${extractableCount} CV${extractableCount === 1 ? "" : "s"}?`}
        description={`This takes ${formatMinutes(etaFor(extractableCount))}. It runs on the server, so you can close this page and come back.`}
        cancelLabel="Not now"
        confirmLabel="Extract all"
      >
        <Box sx={{ mt: 1.5 }}>
          <ParserModelPicker value={bulkModel} onChange={setBulkModel} />
        </Box>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmBulkExtract}
        onClose={() => setConfirmBulkExtract(false)}
        onConfirm={extractSelected}
        title={`Extract ${selected.length} CV${selected.length === 1 ? "" : "s"}?`}
        description={`This takes ${formatMinutes(etaFor(selected.length))}. It runs on the server, so you can leave this page.`}
        cancelLabel="Not now"
        confirmLabel="Extract"
      >
        <Box sx={{ mt: 1.5 }}>
          <ParserModelPicker value={bulkModel} onChange={setBulkModel} />
        </Box>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        onConfirm={deleteSelected}
        title={`Delete ${selected.length} CV${selected.length === 1 ? "" : "s"}?`}
        description="They will be removed from your vault. This cannot be undone."
        cancelLabel="Keep"
        confirmLabel="Delete"
        destructive
      />

      <ConfirmDialog
        open={confirmOcrBulk}
        onClose={() => setConfirmOcrBulk(false)}
        onConfirm={ocrSelected}
        title={`Read ${ocrBulkIds.length} scan${ocrBulkIds.length === 1 ? "" : "s"}?`}
        description="Each page is read by the AI, so this costs a little per page."
        cancelLabel="Not now"
        confirmLabel="Read"
      >
        <Box sx={{ mt: 1.5 }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={ocrThenBranded} onChange={(e) => setOcrThenBranded(e.target.checked)} />}
            label={
              <Typography sx={{ fontSize: 12.5, color: "hsl(150 30% 12%)" }}>
                Also write a Squarehub CV from what it reads
              </Typography>
            }
          />
        </Box>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmBrandedBulk}
        onClose={() => setConfirmBrandedBulk(false)}
        onConfirm={generateBrandedSelected}
        title={`Generate ${selected.length} Squarehub CV${selected.length === 1 ? "" : "s"}?`}
        description="Each one is written by the AI and takes up to a minute. It runs on the server, so you can leave this page."
        cancelLabel="Not now"
        confirmLabel="Generate"
      >
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box>
            <Typography sx={{ mb: 0.5, fontSize: 12, color: "hsl(150 10% 46%)" }}>Language</Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {BRANDED_LOCALES.map((locale) => (
                <Button
                  key={locale.value}
                  size="small"
                  disableRipple
                  onClick={() => setBulkLocale(locale.value)}
                  sx={{
                    textTransform: "none",
                    fontSize: 12.5,
                    px: 1.25,
                    minWidth: 0,
                    borderRadius: "8px",
                    color: bulkLocale === locale.value ? "hsl(80 97% 72%)" : "hsl(150 30% 12%)",
                    backgroundColor: bulkLocale === locale.value ? "hsl(150 38% 17%)" : "#ffffff",
                    border: "1px solid hsl(140 8% 88%)",
                    "&:hover": {
                      backgroundColor: bulkLocale === locale.value ? "hsl(150 38% 22%)" : "hsl(140 10% 96%)",
                    },
                  }}
                >
                  {locale.label}
                </Button>
              ))}
            </Box>
          </Box>
          <ParserModelPicker value={bulkModel} onChange={setBulkModel} />
        </Box>
      </ConfirmDialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={removeItem}
        title="Delete this CV?"
        description={`${deleteTarget?.filename || "This CV"} will be removed from your vault.`}
        cancelLabel="Keep"
        confirmLabel="Delete"
        destructive
      />
    </Box>
  );
};

export default CvVaultPage;
